/**
 * FAQアコーディオン（CSS Grid 0fr / 1fr + clip-path）
 */
function initFaqAccordion() {
  const buttons = document.querySelectorAll('.p-faq-contents .c-faq-item__question');
  if (!buttons.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const accordionMs = 400;

  buttons.forEach((button) => {
    const answerId = button.getAttribute('aria-controls');
    const answer = answerId ? document.getElementById(answerId) : null;
    if (!answer) return;

    let closeTimer = null;

    const setAria = (isExpanded) => {
      button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      button.setAttribute('aria-label', isExpanded ? '回答を閉じる' : '回答を表示');
    };

    const clearCloseTimer = () => {
      if (closeTimer === null) return;
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    const openAnswer = () => {
      clearCloseTimer();
      answer.hidden = false;
      setAria(true);

      if (prefersReducedMotion) {
        answer.classList.add('is-open');
        return;
      }

      requestAnimationFrame(() => {
        answer.classList.add('is-open');
      });
    };

    const closeAnswer = () => {
      clearCloseTimer();
      setAria(false);
      answer.classList.remove('is-open');

      if (prefersReducedMotion) {
        answer.hidden = true;
        return;
      }

      const finishClose = () => {
        if (button.getAttribute('aria-expanded') !== 'false') return;
        answer.hidden = true;
      };

      const onEnd = (event) => {
        if (event.propertyName !== 'grid-template-rows') return;
        answer.removeEventListener('transitionend', onEnd);
        clearCloseTimer();
        finishClose();
      };

      answer.addEventListener('transitionend', onEnd);
      closeTimer = window.setTimeout(() => {
        closeTimer = null;
        answer.removeEventListener('transitionend', onEnd);
        finishClose();
      }, accordionMs + 50);
    };

    setAria(false);
    answer.hidden = true;
    answer.classList.remove('is-open');

    button.addEventListener('click', () => {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        closeAnswer();
      } else {
        openAnswer();
      }
    });
  });
}

/**
 * カテゴリフィルターから各セクションへスクロール
 */
function initFaqCategoryScroll() {
  const filterRoot = document.querySelector('.p-faq-filter');
  const contentsRoot = document.querySelector('.p-faq-contents');
  if (!filterRoot || !contentsRoot) return;

  const scrollToCategory = (category) => {
    if (!category) return;

    const section = contentsRoot.querySelector(
      `.p-faq-contents__category[data-category="${category}"]`
    );
    if (!section || !section.id) return;

    const targetId = `#${section.id}`;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      const header = document.querySelector('.js-header');
      const offset = header ? header.offsetHeight : 0;
      const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo(0, top);
      return;
    }

    if (typeof smoothScrollTo === 'function') {
      smoothScrollTo(targetId, 500);
      return;
    }

    const header = document.querySelector('.js-header');
    const offset = header ? header.offsetHeight : 0;
    const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY - offset);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  filterRoot.querySelectorAll('.p-faq-filter__btn[data-category]').forEach((button) => {
    button.addEventListener('click', () => {
      scrollToCategory(button.getAttribute('data-category'));
    });
  });

  const select = filterRoot.querySelector('#faq-category');
  if (select) {
    select.addEventListener('change', () => {
      scrollToCategory(select.value);
    });
  }
}

/**
 * キーワード検索（質問・回答テキストで絞り込み）
 */
function initFaqKeywordSearch() {
  const form = document.querySelector('.p-faq-filter__search');
  const input = document.getElementById('faq-keyword');
  const contents = document.querySelector('.p-faq-contents');
  if (!form || !input || !contents) return;

  const categories = contents.querySelectorAll('.p-faq-contents__category');
  const items = contents.querySelectorAll('.p-faq-contents__item');
  const emptyMessage = document.getElementById('faq-search-empty');
  if (!items.length) return;

  const normalize = (value) => value.replace(/\s+/g, ' ').trim().toLowerCase();

  const getItemText = (item) => {
    const question = item.querySelector('.c-faq-item__question-text');
    const answer = item.querySelector('.c-faq-item__answer-text');
    return normalize(`${question?.textContent || ''} ${answer?.textContent || ''}`);
  };

  const setFilteredHidden = (element, isHidden) => {
    element.hidden = isHidden;
    element.classList.toggle('is-faq-filter-hidden', isHidden);
  };

  const applyFilter = () => {
    const keyword = normalize(input.value);
    let visibleCount = 0;

    items.forEach((item) => {
      const isMatch = keyword === '' || getItemText(item).includes(keyword);
      setFilteredHidden(item, !isMatch);
      if (isMatch) visibleCount += 1;
    });

    categories.forEach((category) => {
      const list = category.querySelector('.p-faq-contents__list');
      const categoryItems = category.querySelectorAll('.p-faq-contents__item');
      const hasList = categoryItems.length > 0;

      if (keyword === '') {
        setFilteredHidden(category, false);
        if (list) setFilteredHidden(list, false);
        return;
      }

      if (!hasList) {
        setFilteredHidden(category, true);
        return;
      }

      const hasVisibleItem = Array.from(categoryItems).some((item) => !item.hidden);
      const hideCategory = !hasVisibleItem;

      setFilteredHidden(category, hideCategory);
      if (list) setFilteredHidden(list, hideCategory);
    });

    if (emptyMessage) {
      emptyMessage.hidden = keyword === '' || visibleCount > 0;
    }
  };

  let debounceTimer = null;

  input.addEventListener('input', () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(applyFilter, 200);
  });

  input.addEventListener('search', () => {
    if (input.value === '') applyFilter();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    window.clearTimeout(debounceTimer);
    applyFilter();

    const firstVisible = contents.querySelector('.p-faq-contents__item:not([hidden])');
    if (firstVisible && input.value.trim() !== '') {
      const header = document.querySelector('.js-header');
      const offset = header ? header.offsetHeight : 0;
      const top = Math.max(0, firstVisible.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top, behavior: 'smooth' });
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initFaqAccordion();
  initFaqCategoryScroll();
  initFaqKeywordSearch();
});
